import React, { useState, useEffect } from 'react'
import { Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, Alert } from '@mui/material'
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import PersonOffIcon from '@mui/icons-material/PersonOff'
import PersonIcon from '@mui/icons-material/Person'
import LockResetIcon from '@mui/icons-material/LockReset'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import BlockIcon from '@mui/icons-material/Block'
import { useAuth } from '../../hooks/useAuth'
import api from '../../Services/api'
import { useSnackbar } from 'notistack'

const UsersManagement = () => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [newPassword, setNewPassword] = useState('')
  const { token } = useAuth()
  const { enqueueSnackbar } = useSnackbar()

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const res = await fetch(api.apiUrl(api.API_ENDPOINTS.ADMIN.USERS), {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setUsers(data)
      } else {
        throw new Error("Erreur de récupération des utilisateurs")
      }
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggleRole = async (id, currentIsAdmin) => {
    try {
      const res = await fetch(`${api.apiUrl(api.API_ENDPOINTS.ADMIN.USER_ROLE(id))}?is_admin=${!currentIsAdmin}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        enqueueSnackbar("Rôle mis à jour", { variant: 'success' })
        fetchUsers()
      }
    } catch (err) {
      enqueueSnackbar("Erreur lors de la modification du rôle", { variant: 'error' })
    }
  }

  const toggleStatus = async (id, currentIsActive) => {
    try {
      const res = await fetch(`${api.apiUrl(api.API_ENDPOINTS.ADMIN.USER_STATUS(id))}?is_active=${!currentIsActive}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        enqueueSnackbar("Statut mis à jour", { variant: 'success' })
        fetchUsers()
      }
    } catch (err) {
      enqueueSnackbar("Erreur lors de la modification du statut", { variant: 'error' })
    }
  }

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      enqueueSnackbar("Le mot de passe doit faire au moins 4 caractères", { variant: 'warning' })
      return
    }
    try {
      const res = await fetch(`${api.apiUrl(api.API_ENDPOINTS.ADMIN.USER_RESET_PASSWORD(selectedUser.id))}?new_password=${encodeURIComponent(newPassword)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        enqueueSnackbar("Mot de passe réinitialisé avec succès", { variant: 'success' })
        setPasswordDialogOpen(false)
        setNewPassword('')
      } else {
        throw new Error("Erreur")
      }
    } catch (err) {
      enqueueSnackbar("Erreur lors de la réinitialisation", { variant: 'error' })
    }
  }

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'nickname', headerName: 'Pseudo', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1.5 },
    { 
      field: 'is_admin', 
      headerName: 'Rôle', 
      width: 120,
      renderCell: (params) => (
        <Chip 
          icon={params.value ? <AdminPanelSettingsIcon /> : <PersonIcon />} 
          label={params.value ? 'Admin' : 'Utilisateur'} 
          color={params.value ? 'secondary' : 'default'}
          size="small"
        />
      )
    },
    { 
      field: 'is_active', 
      headerName: 'Statut', 
      width: 120,
      renderCell: (params) => (
        <Chip 
          icon={params.value ? <CheckCircleIcon /> : <BlockIcon />} 
          label={params.value ? 'Actif' : 'Désactivé'} 
          color={params.value ? 'success' : 'error'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 180,
      getActions: (params) => [
        <GridActionsCellItem
          icon={params.row.is_admin ? <PersonIcon /> : <AdminPanelSettingsIcon />}
          label={params.row.is_admin ? "Révoquer Admin" : "Accorder Admin"}
          onClick={() => toggleRole(params.id, params.row.is_admin)}
          showInMenu
        />,
        <GridActionsCellItem
          icon={params.row.is_active ? <PersonOffIcon /> : <CheckCircleIcon />}
          label={params.row.is_active ? "Désactiver compte" : "Activer compte"}
          onClick={() => toggleStatus(params.id, params.row.is_active)}
          showInMenu
        />,
        <GridActionsCellItem
          icon={<LockResetIcon />}
          label="Réinitialiser mot de passe"
          onClick={() => {
            setSelectedUser(params.row)
            setPasswordDialogOpen(true)
          }}
          showInMenu
        />,
      ],
    },
  ]

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>Gestion des Utilisateurs</Typography>
      <DataGrid
        rows={users}
        columns={columns}
        autoHeight
        sx={{
          border: 0,
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'background.default',
          },
        }}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        pageSizeOptions={[5, 10, 20]}
        loading={loading}
        disableRowSelectionOnClick
      />

      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}>
        <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
            Vous êtes sur le point de forcer un nouveau mot de passe pour {selectedUser?.nickname}.
          </Alert>
          <TextField
            autoFocus
            margin="dense"
            label="Nouveau mot de passe"
            type="password"
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleResetPassword} color="error" variant="contained">Réinitialiser</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default UsersManagement
